import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { Toaster } from './components/ui/sonner';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { GlobalAudioPlayer } from './components/GlobalAudioPlayer';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SubmitPage from './pages/SubmitPage';
import RosterPage from './pages/RosterPage';
import NewsPage from './pages/NewsPage';
import ShopPage from './pages/ShopPage';
import WaitlistPage from './pages/WaitlistPage';
import CheckoutPage from './pages/CheckoutPage';
import MusicPage from './pages/MusicPage';
import BackOfficePage from './pages/BackOfficePage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import AuthCallback from './pages/AuthCallback';
import Lenis from '@studio-freight/lenis';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Handle OAuth callback - check URL fragment SYNCHRONOUSLY during render
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check for session_id BEFORE rendering normal routes
  // This prevents race conditions with AuthProvider
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/submit" element={<SubmitPage />} />
        <Route path="/roster" element={<RosterPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/waitlist" element={<WaitlistPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/music" element={<MusicPage />} />
        <Route path="/backoffice" element={<BackOfficePage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <AudioPlayerProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="App min-h-screen bg-[#1A1412] text-[#FDFBF7] pb-24">
            <AppRouter />
            <GlobalAudioPlayer />
            <Toaster position="top-center" />
          </div>
        </BrowserRouter>
      </AudioPlayerProvider>
    </AuthProvider>
  );
}

export default App;